/* ForgotPassword — user enters their email, we always say "if it exists,
   a link is on the way". No enumeration. */
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Loader2, Mail, ArrowRight, Check } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
    } catch {
      /* always treat as success — never reveal whether the account exists */
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px]" data-testid="forgot-password-page">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-xl bg-foreground text-accent grid place-items-center font-bold">v</div>
            <span className="font-semibold tracking-tight">verqo</span>
          </Link>

          {!sent ? (
            <>
              <h1 className="display text-4xl sm:text-5xl mt-2 leading-[1.05] tracking-tight">Reset your password</h1>
              <p className="text-foreground/65 mt-3 text-[14px]">
                Enter the email on your verqo account and we'll send you a link to set a new password.
              </p>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label className="text-[12px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5 block">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-foreground/40 absolute top-1/2 -translate-y-1/2 left-3"/>
                    <input
                      type="email"
                      autoFocus
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      data-testid="forgot-email"
                      className="w-full h-12 rounded-xl border border-foreground/15 bg-white pl-10 pr-3 text-sm focus:outline-none focus:border-foreground/45"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit"
                  className="w-full h-12 rounded-full bg-foreground text-background font-medium inline-flex items-center justify-center gap-2 hover:bg-foreground/90 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                  {loading ? "Sending…" : <>Send reset link <ArrowRight className="w-3.5 h-3.5"/></>}
                </button>
              </form>
              <p className="text-[12.5px] text-foreground/55 mt-6">
                Remembered it? <Link to="/login" className="text-foreground hover:underline">Back to sign in</Link>
              </p>
            </>
          ) : (
            <div data-testid="forgot-sent">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mb-5">
                <Check className="w-5 h-5 text-emerald-700"/>
              </div>
              <h1 className="display text-3xl leading-[1.1] tracking-tight">Check your inbox</h1>
              <p className="text-foreground/65 mt-3 text-[14px] leading-relaxed">
                If an account exists with that email, a reset link is on its way.
                The link expires in <strong>1 hour</strong>.
              </p>
              <p className="text-[12.5px] text-foreground/55 mt-6">
                Didn't get it? Check your spam folder, or{" "}
                <button onClick={() => setSent(false)} className="text-foreground hover:underline" data-testid="forgot-try-again">try a different email</button>.
              </p>
              <Link
                to="/login"
                className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-foreground/70 hover:text-foreground"
              >
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="hidden md:block bg-foreground"/>
    </div>
  );
}
